import { Typography } from '@mui/material';
import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from 'react-query';

import {
  CrudTable,
  DataGridColumnDefinition,
  StylizedNumber,
} from 'components';
import { TranslationNamespace } from 'i18n';
import {
  PayinCreateRequestsHealthRow,
  PayinIntegrationHealth,
  PayinWebhooksHealthRow,
} from 'types';
import { p2pProviderUtils } from 'utils';

type Props = {
  queryResult: UseQueryResult<PayinIntegrationHealth>;
};

const EMPTY_VALUE = '—';

export const IntegrationHealthBlock: React.FC<Props> = ({ queryResult }) => {
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.statistics.provider_conversion.integration_health',
  });

  const createRequestsQueryResult = useMemo(
    () =>
      ({
        ...queryResult,
        data: queryResult.data?.createRequests,
      } as UseQueryResult<PayinCreateRequestsHealthRow[]>),
    [queryResult],
  );

  const webhooksQueryResult = useMemo(
    () =>
      ({
        ...queryResult,
        data: queryResult.data?.webhooks,
      } as UseQueryResult<PayinWebhooksHealthRow[]>),
    [queryResult],
  );

  const createRequestsColumns = useMemo(
    (): DataGridColumnDefinition<PayinCreateRequestsHealthRow>[] => [
      {
        header: t('create_requests.columns.provider'),
        valueGetter: (item) => item.providerName || EMPTY_VALUE,
      },
      {
        header: t('create_requests.columns.attempts'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.attemptsCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('create_requests.columns.no_response'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.noResponseCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('create_requests.columns.http_errors'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.httpErrorCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('create_requests.columns.error_share'),
        valueGetter: (item) => (
          <StylizedNumber value={item.errorShare} unit="%" />
        ),
      },
      {
        header: t('create_requests.columns.avg_duration'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.avgDurationMs}
            unit={tCommon('units.ms')}
          />
        ),
      },
      {
        header: t('create_requests.columns.p90_duration'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.p90DurationMs}
            unit={tCommon('units.ms')}
          />
        ),
      },
    ],
    [t, tCommon],
  );

  const webhooksColumns = useMemo(
    (): DataGridColumnDefinition<PayinWebhooksHealthRow>[] => [
      {
        header: t('webhooks.columns.provider'),
        valueGetter: (item) =>
          item.providerName || t('webhooks.unknown_provider'),
      },
      {
        header: t('webhooks.columns.type'),
        valueGetter: (item) =>
          item.type ? p2pProviderUtils.getTypeLabel(item.type) : EMPTY_VALUE,
      },
      {
        header: t('webhooks.columns.webhooks'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.webhooksCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('webhooks.columns.parse_errors'),
        valueGetter: (item) => (
          <StylizedNumber
            value={item.parseErrorCount}
            unit={tCommon('units.items')}
          />
        ),
      },
      {
        header: t('webhooks.columns.parse_error_share'),
        valueGetter: (item) => (
          <StylizedNumber value={item.parseErrorShare} unit="%" />
        ),
      },
    ],
    [t, tCommon],
  );

  return (
    <Fragment>
      <Typography variant="h6" sx={{ mt: 6, mb: 2 }}>
        {t('title')}
      </Typography>
      <Typography variant="subtitle1" sx={{ mt: 4, mb: 2 }}>
        {t('create_requests.title')}
      </Typography>
      <CrudTable
        queryResult={createRequestsQueryResult}
        columns={createRequestsColumns}
      />
      <Typography variant="subtitle1" sx={{ mt: 4, mb: 2 }}>
        {t('webhooks.title')}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
        {t('webhooks.hint')}
      </Typography>
      <CrudTable queryResult={webhooksQueryResult} columns={webhooksColumns} />
    </Fragment>
  );
};
